import React, { useState, useRef } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import CloseIcon from "@material-ui/icons/Close";
import ViewIcon from "@material-ui/icons/Visibility";
import OffIcon from "@material-ui/icons/VisibilityOff";
import RefreshIcon from "@material-ui/icons/Refresh";
import ReCaptcha from "./Recaptcha";

const viz_url = process.env.REACT_APP_VIZ_URL;

const useStyles = makeStyles((theme) => ({
  signInButton: {
    backgroundColor: "#1565C0",
    color: "#ffffff",
    textTransform: "none",
    fontSize: "1rem",
    fontWeight: 500,
    padding: "12px",
    "&:hover": {
      backgroundColor: "#0d47a1",
    },
  },
  ssoButton: {
    textTransform: "none",
    padding: "10px 12px",
    fontSize: "0.875rem",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  ssoIconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    marginRight: theme.spacing(1),
    flexShrink: 0,
  },
  ssoButtonContent: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  ssoButtonText: {
    fontSize: "0.75rem",
    lineHeight: 1.3,
    textAlign: "left",
  },
  divider: {
    margin: theme.spacing(2, 0),
    textAlign: "center",
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: "50%",
      left: 0,
      right: 0,
      height: 1,
      backgroundColor: "#e0e0e0",
    },
  },
  dividerText: {
    position: "relative",
    display: "inline-block",
    padding: theme.spacing(0, 2),
    backgroundColor: "#ffffff",
    color: "#757575",
    fontSize: "0.875rem",
  },
  forgotPasswordLink: {
    color: "#1565C0",
    textTransform: "none",
    padding: 0,
    fontSize: "1rem",
    "&:hover": {
      backgroundColor: "transparent",
      textDecoration: "underline",
    },
  },
  rememberMeContainer: {
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(1),
  },
  rememberMeText: {
    fontSize: "0.875rem",
    color: "#424242",
  },
}));

const LoginForm = ({
  onSubmit,
  classes,
  errorMessage,
  open,
  setErrorMessage,
  persistentAlert,
  handleChangeLocation,
  onSubmitWithRecaptcha,
}) => {
  const formClasses = useStyles();
  const [token, setToken] = useState('');
  const [submitCount, setSubmitCount] = useState(0);
  const [recaptchaKey, setRecaptchaKey] = useState(0);
  const recaptchaRef = useRef(null);

  const handleToken = (token) => {
    setToken(token);
  };

  const handleRefreshRecaptcha = () => {
    // Reset the token
    setToken('');
    // Force re-render of reCAPTCHA component by changing key
    setRecaptchaKey(prev => prev + 1);
    // If you have access to reCAPTCHA reset method, you can also call it
    if (recaptchaRef.current && recaptchaRef.current.reset) {
      recaptchaRef.current.reset();
    }
  };

  const handleFormSubmit = (values) => {
    const newCount = submitCount + 1;
    setSubmitCount(newCount);

    if (newCount > 5) {
      if (token) {
        onSubmitWithRecaptcha({ ...values, token });
        // Reset reCAPTCHA after submission for potential retry
        setTimeout(() => {
          handleRefreshRecaptcha();
        }, 2000);
      } else {
        // Don't submit if no token available
        return;
      }
    } else {
      onSubmit({ ...values });
    }
  };

  let disablingCondition = false;
  if (submitCount > 5 && token === '') {
    disablingCondition = true;
  }

  return (
    <Formik
      initialValues={{
        email: "",
        password: "",
        checked: false,
        visible: false,
      }}
      validationSchema={Yup.object().shape({
        email: Yup.string()
          .email("Must be a valid email.")
          .max(255)
          .required("Email is required."),
        password: Yup.string().max(255).required("Password is required."),
      })}
      onSubmit={handleFormSubmit}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        touched,
        values,
        setFieldValue,
      }) => (
        <form
          noValidate
          onSubmit={handleSubmit}
          className={classes.formStyle}
          style={{ paddingTop: "5%" }}
        >
          {errorMessage && (
            <Collapse in={open}>
              <Alert
                severity="error"
                variant="filled"
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => setErrorMessage("")}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
              >
                <Typography variant="body1">{errorMessage}</Typography>
              </Alert>
            </Collapse>
          )}
          <Box mb={2}>
            <Typography variant="body2" style={{ marginBottom: "8px", color: "#424242", fontSize: "0.875rem" }}>
              Email Address
            </Typography>
            <TextField
              error={Boolean(touched.email && errors.email)}
              fullWidth
              helperText={touched.email && errors.email}
              placeholder="Enter email address"
              name="email"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.email}
              variant="outlined"
            />
          </Box>
          <Box mb={2}>
            <Typography variant="body2" style={{ marginBottom: "8px", color: "#424242", fontSize: "0.875rem" }}>
              Password
            </Typography>
            <TextField
              error={Boolean(touched.password && errors.password)}
              fullWidth
              helperText={touched.password && errors.password}
              placeholder="Enter password"
              name="password"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.password}
              variant="outlined"
              type={values.visible ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setFieldValue("visible", !values.visible, false)}
                  >
                    {values.visible ? <OffIcon /> : <ViewIcon />}
                  </IconButton>
                ),
              }}
            />
          </Box>
          <Box mt={3}>
            <Button
              disabled={disablingCondition}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              className={formClasses.signInButton}
            >
              Sign in
            </Button>
          </Box>
          <Box className={formClasses.rememberMeContainer} style={{ marginTop: "24px" }}>
            <Checkbox
              checked={values.checked}
              color="primary"
              className={classes.rememberMeCheck}
              onChange={handleChange}
              name="checked"
            />
            <Typography className={formClasses.rememberMeText}>
              Remember me
            </Typography>
            <Box flex={1} />
            <Button
              disabled={
                persistentAlert !== "" &&
                persistentAlert.message === "Signing in..."
              }
              onClick={() => handleChangeLocation("forgotPass")}
              className={formClasses.forgotPasswordLink}
            >
              Forgot Password?
            </Button>
          </Box>
          {submitCount > 5 && (
            <Box mt={2}>
              <Box display="flex" alignItems="center" gap={1}>
                {/* <IconButton
                  onClick={handleRefreshRecaptcha}
                  color="primary"
                  title="Refresh reCAPTCHA"
                  size="small"
                >
                  <RefreshIcon />
                </IconButton> */}
                <Box flex={1}>
                  <ReCaptcha
                    key={recaptchaKey} // Force re-render when key changes
                    ref={recaptchaRef}
                    siteKey={"6LeEEwYrAAAAAMRPP6WIE60QON6JPXKpJH8rRZBA"}
                    callback={handleToken}
                  />
                </Box>

              </Box>
              {/* <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
                Click the refresh button to get a new reCAPTCHA if needed
              </Typography> */}
            </Box>
          )}
          <Box className={formClasses.divider}>
            <Typography className={formClasses.dividerText}>
              Or sign in with
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                href={`${viz_url}/api/auth/salesforce`}
                className={formClasses.ssoButton}
              >
                <Box className={formClasses.ssoButtonContent}>
                  <Box className={formClasses.ssoIconWrapper}>
                    <img
                      src="https://skygeniwebapp.blob.core.windows.net/login-templates/salesforce.png"
                      alt="Salesforce"
                      width="24"
                      height="24"
                      style={{ objectFit: 'contain' }}
                    />
                  </Box>
                  <span className={formClasses.ssoButtonText}>Sign in with Salesforce</span>
                </Box>
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                href={`${viz_url}/api/auth/activeDirectory`}
                className={formClasses.ssoButton}
              >
                <Box className={formClasses.ssoButtonContent}>
                  <Box className={formClasses.ssoIconWrapper}>
                    <img
                      src="https://skygeniwebapp.blob.core.windows.net/login-templates/microsoft.png"
                      alt="Microsoft"
                      width="24"
                      height="24"
                      style={{ objectFit: 'contain' }}
                    />
                  </Box>
                  <span className={formClasses.ssoButtonText}>Sign in with Microsoft</span>
                </Box>
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
};

export default LoginForm;